import { EFinancingProgram } from '@if/core/types'
import { type QueryObserverResult } from '@tanstack/react-query'
import { createContext, useContext, useMemo, type FC, type ReactNode } from 'react'
import { useGetMessageList } from '../data/api/message/message-api'
import { FullCreditApplication } from '../data/types'

interface CreditApplicationContextValue {
  fullCreditApp: FullCreditApplication | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => Promise<QueryObserverResult<FullCreditApplication, Error>>
  id: string
  financingProgramId: EFinancingProgram
}

const CreditApplicationContext = createContext<CreditApplicationContextValue | null>(null)

interface CreditApplicationProviderProps {
  children: ReactNode
  value: CreditApplicationContextValue
}

export const CreditApplicationProvider: FC<CreditApplicationProviderProps> = ({ children, value }) => {
  return <CreditApplicationContext.Provider value={value}>{children}</CreditApplicationContext.Provider>
}

export function useCreditApplicationContext() {
  const context = useContext(CreditApplicationContext)
  if (!context) {
    throw new Error('useCreditApplicationContext must be used within a CreditApplicationProvider')
  }
  return context
}

/**
 * Hook to access the full credit application data from React Query
 */
export function useCreditApplicationData() {
  const { fullCreditApp, isLoading, isError, refetch, id, financingProgramId } = useCreditApplicationContext()

  // Load messages with React Query
  const [messageList] = useGetMessageList(id, financingProgramId)

  // Memoize messages and private messages to avoid unnecessary re-renders
  const messages = useMemo(() => messageList ?? [], [messageList])
  const privateMessages = useMemo(() => messages.filter((m) => m.isPrivate), [messages])

  return {
    fullCreditApp,
    creditApplication: fullCreditApp?.credit,
    worksheet: fullCreditApp?.worksheet,
    tasks: fullCreditApp?.tasks ?? [],
    progressionStatus: fullCreditApp?.progressionStatus,
    incomeConfirmed: fullCreditApp?.incomeConfirmed === true,
    flinksIncomes: fullCreditApp?.flinksIncomes ?? [],
    fraudAnalysis: fullCreditApp?.fraudAnalysis ?? null,
    fraudFlags: fullCreditApp?.fraudFlags ?? null,
    documents: fullCreditApp?.documents ?? [],
    requiredDocuments: fullCreditApp?.requiredDocuments ?? [],
    requiredExternalSteps: fullCreditApp?.requiredExternalSteps ?? [],
    electronicSignatures: fullCreditApp?.electronicSignatures ?? [],
    messages,
    privateMessages,
    isLoading,
    isError,
    refetch,
  }
}
